import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  orderBy,
} from 'firebase/firestore/lite';

import contentConverter from './contentConverter';
import { ContentData } from '../types/contentTypes';
import { firestore } from '../utils/firebase';

export type NewContentData = Omit<ContentData, 'id'>;

export default class ContentApi {
  private static readonly collectionReference = collection(firestore, 'contents').withConverter(contentConverter);

  static async addContent(newContent: NewContentData) {
    const docRef = doc(this.collectionReference);
    await setDoc(docRef, { ...newContent, id: docRef.id } as ContentData);
    return docRef.id;
  }

  public static async getContent(id: string): Promise<ContentData> {
    const docRef = doc(this.collectionReference, id);
    const docSnap = await getDoc(docRef);
    return docSnap.data()!;
  }

  public static async getContents() {
    const querySnap = await getDocs(this.collectionReference);
    return querySnap.docs.map(docSnap => docSnap.data());
  }

  public static async getContentsBySectionId(sectionId: string): Promise<ContentData[]> {
    const querySnaps = await getDocs(
      query(this.collectionReference, where('sectionId', '==', sectionId), orderBy('order', 'asc'))
    );
    return querySnaps.docs.map(docSnap => docSnap.data() as ContentData);
  }

  public static async getContentsBySkillId(skillId: string): Promise<ContentData[]> {
    const querySnaps = await getDocs(query(this.collectionReference, where('skillId', '==', skillId)));
    return querySnaps.docs.map(docSnap => docSnap.data());
  }

  static updateContent(content: ContentData) {
    const docRef = doc(this.collectionReference, content.id);
    // Firestore doesn't use the converter on update for some unknown reason, so we manually convert.
    return updateDoc(docRef, contentConverter.toFirestore(content));
  }

  static async setContent(content: ContentData) {
    const docRef = doc(this.collectionReference, content.id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      throw new Error('Document does not exist');
    }
    await setDoc(docRef, content);
    return docRef.id;
  }

  public static async updateContents(contents: ContentData[]) {
    const batch = writeBatch(firestore);
    contents.forEach(content => {
      const docRef = doc(this.collectionReference, content.id);
      // Firestore doesn't use the converter on update for some unknown reason, so we manually convert.
      batch.update(docRef, contentConverter.toFirestore(content));
    });
    return await batch.commit();
  }

  static async removeContent(content: ContentData) {
    const docRef = doc(this.collectionReference, content.id);
    return await deleteDoc(docRef);
  }
}
